/**
 * Back Order Service — the storefront "back order cart".
 *
 * Mirrors the regular cart, but persists to BackOrders instead of a DraftCartOrder. Each product is
 * its own OPEN BackOrder (so each carries the date it was back-ordered), so the "cart" view here
 * aggregates the items across all of the client's open back orders. Back orders capture demand
 * beyond current stock: quantities are NOT capped to availability. Editing auto-persists (no separate
 * "place" step) — back orders are created/updated live via POST /storefront/back-orders/set-quantity.
 */
import { apiService, tokenManager } from "./api.js";
import cartService from "./cartServices.js";

class BackOrderService {
  _getCurrentUserInfo() {
    const store = tokenManager.getCurrentStore();
    const company = tokenManager.getCurrentCompany();
    if (!store?.id) {
      throw new Error("No store selected. Please select a store to manage back orders.");
    }
    return { clientId: store.id, companyId: company?.id, storeName: store.name };
  }

  /**
   * Load the customer's back-order "cart" — the items across ALL of their open back orders (one
   * product per back order), enriched with fresh product data (name, flavor, price, availability)
   * the same way the cart is. Newest-back-ordered first. Returns { backOrderItems: [] } when none.
   */
  async getOpenBackOrders() {
    const { clientId } = this._getCurrentUserInfo();

    const response = await apiService.get("/storefront-odata/StorefrontBackOrders", {
      $filter: `OptBackOrderStatusID eq 1 and ClientID eq ${clientId}`,
      $orderby: "DateCreated desc",
    });

    // One back order == one product, so each open back order is itself a cart line.
    const backOrders = response?.value || [];
    const items = backOrders.map((bo) => ({
      id: bo.id,
      backOrderID: bo.id,
      productSkuVariantID: bo.productSkuVariantID,
      unitPrice: bo.unitPrice,
      unitQuantity: bo.unitQuantity,
      singleUnitFee: bo.singleUnitFee,
      isSample: bo.isSample,
      dateCreated: bo.dateCreated,
    }));
    if (items.length === 0) {
      return { backOrderItems: [] };
    }

    // Merge current product data (reuses the cart's enrichment so name/price/availability match).
    const ids = [...new Set(items.map((i) => i.productSkuVariantID))];
    const productData = await cartService._fetchProductDataForCart(ids);
    const backOrderItems = items.map((item) => {
      const p = productData.find((pd) => pd.id === item.productSkuVariantID);
      return p ? { ...item, productSkuVariant: p } : item;
    });

    return { backOrderItems };
  }

  /**
   * Set (create/update/remove) the back-order quantity for a single variant. Quantity 0 cancels the
   * variant's back order. Auto-persisted — no place step.
   */
  async setItemQuantity(productSkuVariantId, unitQuantity, productData = {}) {
    const { clientId } = this._getCurrentUserInfo();

    return apiService.post("/storefront/back-orders/set-quantity", {
      clientID: clientId,
      productSkuVariantID: productSkuVariantId,
      unitQuantity,
      unitPrice:
        productData.discountUnitPrice && productData.discountUnitPrice > 0
          ? productData.discountUnitPrice
          : productData.unitPrice ?? null,
    });
  }
}

export default new BackOrderService();
